import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { from, of, Observable } from 'rxjs';
import { map as rxMap, shareReplay as rxShareReplay, switchMap as rxSwitchMap } from 'rxjs/operators';

export interface BlogTocItem {
  id: string;
  label: string;
}

export interface BlogDoc {
  html: SafeHtml;
  toc: BlogTocItem[];
  rawText: string;
}

export interface BlogPostMeta {
  slug: string;
  title: string;
  excerpt: string;
  tagIds: string[];
  publishedAt: string;
  thumbnailUrl: string;
  contentPath: string;
  seo: {
    metaTitle?: string;
    metaDescription?: string;
  };
  author: {
    name?: string;
    linkedin?: string;
    profilePic?: string;
  };
}

interface BlogIndex {
  blogs: Array<
    BlogPostMeta & {
      id: number;
    }
  >;
  tagMap: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class BlogDocsService {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly indexPath = '/assets/blogs/index.json';
  private readonly legacyConfigPath = '/assets/blogs/docx-config.json';

  private readonly index$: Observable<BlogIndex> = this.createIndexStream();
  private readonly htmlCache = new Map<string, string>();

  getAllPosts() {
    return this.index$.pipe(rxMap((idx) => idx.blogs || []));
  }

  getPostMetaBySlug(slug: string) {
    return this.getAllPosts().pipe(
      rxMap((posts: BlogPostMeta[]) => posts.find((p) => this.normalize(p.slug) === this.normalize(slug)) || null),
      rxShareReplay(1)
    );
  }

  getDocBySlug(slug: string) {
    return this.getPostMetaBySlug(slug).pipe(
      rxSwitchMap((meta: BlogPostMeta | null) => {
        if (!meta) {
          return of<BlogDoc>({
            html: this.sanitizer.bypassSecurityTrustHtml('<p>Blog post not found.</p>'),
            toc: [],
            rawText: '',
          });
        }

        return from(this.loadDocxAsHtml(meta.contentPath)).pipe(
          rxMap((rawHtml) => {
            const promoted = this.promoteStrongParagraphHeadings(this.normalizeDocxHtml(rawHtml));
            const normalizedLists = this.flattenRedundantNestedLists(promoted);
            const normalized = this.addHeadingIds(normalizedLists);
            const toc = this.extractTocFromContent(normalized);
            const rawText = normalized.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            return {
              html: this.sanitizer.bypassSecurityTrustHtml(normalized),
              toc,
              rawText,
            };
          })
        );
      }),
      rxShareReplay(1)
    );
  }

  getTagMap() {
    return this.index$.pipe(rxMap((idx) => idx.tagMap || {}));
  }

  private createIndexStream(): Observable<BlogIndex> {
    if (!isPlatformBrowser(this.platformId)) {
      return of<BlogIndex>({ blogs: [], tagMap: {} });
    }

    return from(
      fetch(this.indexPath, { cache: 'no-store' }).then(async (r) => {
        if (r.ok) return r.json();
        return null;
      })
    ).pipe(
      rxSwitchMap((raw) => {
        if (raw && typeof raw === 'object' && 'blogs' in (raw as any)) return of(raw as BlogIndex);
        return from(fetch(this.legacyConfigPath, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : []))).pipe(
          rxMap((legacyRaw) => (Array.isArray(legacyRaw) ? legacyRaw : [])),
          rxMap((entries: Array<{ slug?: string; title?: string; file?: string }>) => {
            const blogs = entries
              .map((e, idx) => {
                const file = String(e.file || '').trim();
                const slug = String(e.slug || '').trim();
                const title = String(e.title || '').trim() || this.getTitleFromFileName(file);
                return {
                  id: idx + 1,
                  slug,
                  title,
                  excerpt: '',
                  tagIds: [],
                  publishedAt: '',
                  thumbnailUrl: '',
                  contentPath: `/assets/blogs/${encodeURIComponent(file)}`,
                  seo: { metaTitle: '', metaDescription: '' },
                  author: { name: '', linkedin: '', profilePic: '' },
                };
              })
              .filter((b) => !!b.slug && !!b.contentPath);
            return { blogs, tagMap: {} } as BlogIndex;
          })
        );
      }),
      rxMap((idx: any) => ({
        blogs: (Array.isArray(idx.blogs) ? idx.blogs : []).map((b: any) => ({
          slug: String(b?.slug || '').trim(),
          title: String(b?.title || '').trim(),
          excerpt: String(b?.excerpt || '').trim(),
          tagIds: Array.isArray(b?.tagIds) ? b.tagIds.map((t: any) => String(t || '').trim()).filter(Boolean) : [],
          publishedAt: String(b?.publishedAt || '').trim(),
          thumbnailUrl: String(b?.thumbnailUrl || '').trim(),
          contentPath: String(b?.contentPath || '').trim(),
          seo: {
            metaTitle: String(b?.seo?.metaTitle || '').trim(),
            metaDescription: String(b?.seo?.metaDescription || '').trim(),
          },
          author: {
            name: String(b?.author?.name || '').trim(),
            linkedin: String(b?.author?.linkedin || '').trim(),
            profilePic: String(b?.author?.profilePic || '').trim(),
          },
        })),
        tagMap: idx.tagMap && typeof idx.tagMap === 'object' ? (idx.tagMap as Record<string, string[]>) : {},
      })),
      rxShareReplay(1)
    );
  }

  private async loadDocxAsHtml(contentPath: string): Promise<string> {
    const cacheKey = contentPath;
    const cached = this.htmlCache.get(cacheKey);
    if (cached) return cached;

    try {
      const response = await fetch(contentPath, { cache: 'no-store' });
      // In dev, missing static assets can still return index.html (200 text/html) due to SPA fallback.
      // Mammoth expects a real DOCX (zip) binary, so detect and surface a clear error.
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (!response.ok) {
        return `<p>Document not found. Missing DOCX: <code>${this.escapeHtml(contentPath)}</code> (HTTP ${response.status}).</p>`;
      }
      if (contentType.includes('text/html')) {
        return `<p>Document not found. The server returned HTML for: <code>${this.escapeHtml(
          contentPath
        )}</code>. Ensure the DOCX exists under <code>public${this.escapeHtml(contentPath)}</code>.</p>`;
      }

      // Use the browser bundle for correct client-side behavior (matches the source repo).
      const mammothModule = await import('mammoth/mammoth.browser');
      const mammoth = (mammothModule as any).default ?? mammothModule;
      const arrayBuffer = await response.arrayBuffer();
      // DOCX is a zip; it should start with "PK".
      const header = new Uint8Array(arrayBuffer.slice(0, 2));
      if (!(header[0] === 0x50 && header[1] === 0x4b)) {
        return `<p>Document not found. Invalid DOCX payload for: <code>${this.escapeHtml(
          contentPath
        )}</code>. (Expected a .docx file, got ${this.escapeHtml(contentType || 'unknown')}).</p>`;
      }
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const rawHtml = result.value || '';

      const looksVisuallyEmpty = (() => {
        const textOnly = rawHtml.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
        const hasImg = /<img\b/i.test(rawHtml);
        return !textOnly && !hasImg;
      })();

      const html = rawHtml
        ? looksVisuallyEmpty
          ? '<p>No content could be extracted from this file.</p>'
          : rawHtml
        : '<p>No content could be extracted from this file.</p>';

      this.htmlCache.set(cacheKey, html);
      return html;
    } catch {
      return `<p>Unable to render this document. If this is a DOCX, verify it downloads from <code>${this.escapeHtml(
        contentPath
      )}</code>.</p>`;
    }
  }

  private escapeHtml(text: string): string {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private normalizeDocxHtml(rawHtml: string): string {
    if (!rawHtml) return rawHtml;
    if (!isPlatformBrowser(this.platformId)) return rawHtml;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, 'text/html');
      const body = doc.body;

      body.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
        const style = el.getAttribute('style');
        if (!style) return;
        const cleanedStyle = style
          .replace(/(^|;)\s*(width|max-width|min-width|height|max-height|min-height)\s*:[^;]+/gi, '$1')
          .replace(/(^|;)\s*margin(-top|-right|-bottom|-left)?\s*:[^;]+/gi, '$1')
          .replace(/;;+/g, ';')
          .replace(/^;|;$/g, '')
          .trim();
        if (cleanedStyle) el.setAttribute('style', cleanedStyle);
        else el.removeAttribute('style');
      });

      body.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      });

      return body.innerHTML || rawHtml;
    } catch {
      return rawHtml;
    }
  }

  private promoteStrongParagraphHeadings(content: string): string {
    if (!content) return content;
    if (!isPlatformBrowser(this.platformId)) return content;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      const ps = Array.from(doc.body.querySelectorAll('p'));
      for (const p of ps) {
        const fullText = (p.textContent || '').replace(/\s+/g, ' ').trim();
        if (!fullText) continue;

        const firstEl = p.firstElementChild as HTMLElement | null;
        if (!firstEl || firstEl.tagName.toLowerCase() !== 'strong') continue;

        const strong = firstEl;
        const strongText = (strong.textContent || '').replace(/\s+/g, ' ').trim();
        if (!strongText) continue;

        const restText = (() => {
          const clone = p.cloneNode(true) as HTMLElement;
          const first = clone.firstElementChild;
          if (first && first.tagName.toLowerCase() === 'strong') first.remove();
          return (clone.textContent || '').replace(/\s+/g, ' ').trim();
        })();

        const isShort = strongText.length <= 36;
        const isAllCaps = strongText.toUpperCase() === strongText && /[A-Z]/.test(strongText);
        const endsWithColon = /:\s*$/.test(strongText);
        const endsWithQuestion = /\?\s*$/.test(strongText);

        const tag = endsWithColon || (isShort && !endsWithQuestion) || isAllCaps ? 'h3' : 'h2';

        if (!restText) {
          const h = doc.createElement(tag);
          h.innerHTML = strong.innerHTML;
          p.replaceWith(h);
          continue;
        }

        const looksLikeHeading = endsWithColon || endsWithQuestion || isShort || isAllCaps;
        if (!looksLikeHeading) continue;

        const h = doc.createElement(tag);
        h.innerHTML = strong.innerHTML;

        const p2 = doc.createElement('p');
        const clone = p.cloneNode(true) as HTMLElement;
        const first = clone.firstElementChild;
        if (first && first.tagName.toLowerCase() === 'strong') first.remove();
        p2.innerHTML = clone.innerHTML.trim();

        p.replaceWith(h, p2);
      }

      return doc.body.innerHTML || content;
    } catch {
      return content;
    }
  }

  private flattenRedundantNestedLists(content: string): string {
    if (!content) return content;
    if (!isPlatformBrowser(this.platformId)) return content;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      let changed = true;
      let guard = 0;
      while (changed && guard++ < 8) {
        changed = false;

        const listItems = Array.from(doc.body.querySelectorAll('li'));
        for (const li of listItems) {
          const elementChildren = Array.from(li.children);
          if (elementChildren.length !== 1) continue;

          const onlyChild = elementChildren[0];
          const tag = onlyChild.tagName.toLowerCase();
          if (tag !== 'ul' && tag !== 'ol') continue;

          const directText = Array.from(li.childNodes)
            .filter((n) => n.nodeType === Node.TEXT_NODE)
            .map((n) => (n.textContent || '').replace(/\s+/g, ' ').trim())
            .join(' ')
            .trim();
          if (directText) continue;

          const nestedLis = Array.from(onlyChild.querySelectorAll(':scope > li'));
          if (nestedLis.length === 0) continue;

          const parentList = li.parentElement;
          if (!parentList || (parentList.tagName.toLowerCase() !== 'ul' && parentList.tagName.toLowerCase() !== 'ol'))
            continue;

          for (const nli of nestedLis) {
            parentList.insertBefore(nli, li);
          }
          li.remove();
          changed = true;
        }
      }

      return doc.body.innerHTML || content;
    } catch {
      return content;
    }
  }

  private extractTocFromContent(content: string): BlogTocItem[] {
    const toc: BlogTocItem[] = [];
    const headingRegex = /<h2[^>]*id="([^"]+)"[^>]*>(.*?)<\/h2>/gi;
    let match: RegExpExecArray | null;
    while ((match = headingRegex.exec(content)) !== null) {
      const label = match[2].replace(/<[^>]+>/g, '').trim();
      if (label) toc.push({ id: match[1], label });
    }
    return toc;
  }

  private addHeadingIds(content: string): string {
    if (!content) return content;
    let headingIndex = 0;
    return content.replace(/<h2([^>]*)>(.*?)<\/h2>/gi, (_full, attrs: string, title: string) => {
      if (/\sid=/.test(attrs)) return `<h2${attrs}>${title}</h2>`;
      const cleanTitle = title.replace(/<[^>]+>/g, '').trim().toLowerCase();
      const slug = cleanTitle
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      const id = slug || `section-${headingIndex++}`;
      return `<h2${attrs} id="${id}">${title}</h2>`;
    });
  }

  private getTitleFromFileName(fileName: string): string {
    const base = (fileName || '').replace(/\.docx$/i, '').trim();
    if (!base) return 'Blog';
    return base
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private normalize(value: string): string {
    return (value || '').toLowerCase().trim();
  }
}

